/**
 * Theme Configuration
 * Default theme for the Audio DAW canvas UI
 */
/**
 * Every colour, font and dimension the canvas draws with.
 *
 * Fonts are **CSS font shorthand**, so weight and style belong in the string —
 * `"bold 14px sans-serif"`, not a separate weight key. There used to be four
 * `*FontWeight` keys here; nothing read them, so setting one silently did
 * nothing, which is a worse trap than not offering it.
 *
 * A host theme is merged over this one, so a partial theme is fine and anything
 * omitted keeps its default. The three nested blocks — `scrollbar`, `scales`,
 * `selectionMenu` — are merged key by key rather than replaced wholesale, so
 * overriding one colour in a block does not blank the rest of it.
 */
export const DEFAULT_THEME = {
  // Background colors
  background: "gray",
  trackBackground: "#f1f1f1",
  trackBackgroundDragging: "rgba(33, 150, 243, 0.2)",
  rulerBackground: "#2c2c2c",
  minimapBackground: "#1a1a1a",
  minimapTrackBackground: "#404040",

  // Grid
  gridColor: "rgba(255, 255, 255, 0.1)",
  gridLineWidth: 1,

  // Waveform
  defaultWaveformColor: "#2196F3",
  waveformLineWidth: 1,
  // Muted, or silent because something else is soloed. Dimmed rather than
  // hidden: the audio is still there and still worth reading.
  inaudibleTrackOpacity: 0.3,
  waveformGainScaling: true, // Enable visual scaling of waveform based on gain
  /**
   * How gain is applied to the drawn height.
   *
   * "soft" is a tanh knee: a boosted waveform never leaves the lane, but the
   * drawn height stops matching the real level near full scale — at unity a
   * full-scale peak draws at 0.76, which reads about 2.4 dB *below* where it
   * really is. That is the wrong direction to be wrong in if you are asking
   * "am I clipping?" against the dB ruler.
   *
   * "linear" scales exactly and clamps at the rail, so every level below full
   * scale reads true on the ruler and anything over pins flat at the edge —
   * which is itself the clipping signal. Default stays "soft" so existing
   * hosts look unchanged.
   */
  waveformGainCurve: "soft", // "soft" | "linear"

  // Spectrogram overlay (drawn over the waveform at the track's own opacity)
  spectrogramColormap: "magma", // "magma" | "viridis" | "gray"
  spectrogramSmoothing: true, // Interpolate when a tier is stretched across pixels
  spectrogramDisplayFloor: -90, // dB shown as the bottom of the colour ramp
  spectrogramDisplayCeiling: -20, // dB shown as the top of the colour ramp

  // Streaming buffer indicator, drawn along the bottom of a streamed track
  bufferedBarHeight: 3,
  bufferedBarColor: "rgba(76, 175, 80, 0.85)",
  stalledBarColor: "rgba(224, 164, 88, 0.9)",

  // Track border (when dragging)
  trackBorderColor: "#2196F3",
  trackBorderWidth: 2,

  // Track labels
  trackLabelColor: "#333",
  trackLabelFont: "12px sans-serif",

  // Track offset display (when dragging)
  offsetDisplayBackground: "rgba(0, 0, 0, 0.7)",
  offsetDisplayColor: "#4CAF50",
  offsetDisplayFont: "12px monospace",

  // Loading indicator
  loadingTextColor: "#333",
  loadingTextFont: "bold 14px sans-serif",

  // Timeline ruler
  rulerTickColor: "#666",
  rulerTextColor: "#ccc",
  rulerTextFont: "10px monospace",
  rulerMajorTickHeight: 38,
  rulerMinorTickHeight: 30,
  rulerTickBottomMargin: 22, // Margin from bottom of canvas where ticks start

  // Playhead
  playheadColor: "#FF4081",
  playheadWidth: 2,

  // Minimap
  minimapWaveformOpacity: 0.6,
  minimapViewportFill: "rgba(255, 255, 255, 0.15)",
  minimapViewportStroke: "#4CAF50",
  minimapViewportStrokeWidth: 2,
  minimapPlayheadColor: "#FF4081",
  minimapPlayheadWidth: 1,

  // Scrollbar (legacy properties for backward compatibility)
  scrollbarTrackColor: "rgba(0, 0, 0, 0.15)",
  scrollbarThumbColor: "rgba(255, 255, 255, 0.4)",
  scrollbarThumbColorActive: "rgba(255, 255, 255, 0.7)",
  scrollbarThumbBorder: "rgba(0, 0, 0, 0.3)",
  scrollbarWidth: 16,

  // Scrollbar configuration object (recommended)
  scrollbar: {
    width: 16, // Thickness of scrollbars (vertical width, horizontal height)
    minThumbSize: 40, // Minimum thumb size in pixels
    trackColor: "rgba(0, 0, 0, 0.15)",
    thumbColor: "rgba(255, 255, 255, 0.4)",
    thumbColorActive: "rgba(255, 255, 255, 0.7)",
    thumbBorder: "rgba(0, 0, 0, 0.3)",
    showVertical: true,
    showHorizontal: true,
    customDrawFunction: null, // Optional: (ctx, geometry, isActive, theme, orientation) => void
  },


  /**
   * Right-click menu on a region.
   *
   * The renderer reads every one of these keys. They were referenced there
   * before they existed here, which made the menu throw on its first line and
   * never appear — so this block is load-bearing, not decoration.
   */
  /**
   * The dB and frequency rulers. They draw onto canvases the host owns and
   * positions, so nothing here is about layout — only ink.
   */
  scales: {
    background: "transparent",
    // Dark enough to read against a mid-grey page as well as a light track
    // lane. The default `background` here is "gray", so anything near #888 for
    // a tick disappears into it entirely.
    textColor: "#1f1f1f",
    centerTextColor: "#000000",
    tickColor: "#2e2e2e",
    minorTickColor: "#4a4a4a",
    centerLineColor: "#111111",
    font: "10px system-ui, sans-serif",
    majorTickLength: 8,
    minorTickLength: 4,
    labelGap: 4,
    /**
     * Which edge of the ruler's own canvas the ticks sit against.
     *
     * A ruler to the *left* of the waveform wants "right", so its ticks run up
     * against the lane; one on the right wants "left". With `padding: 0` they
     * touch it, which is what makes a level or a frequency readable straight
     * across.
     */
    tickAlign: "left", // "left" | "right"
    /** Inset from the aligned edge. 0 puts the ticks flush against it. */
    padding: 2,
    /** Clearance between labels, in line heights — below this they are culled. */
    minimumLabelGap: 1.6,
    /**
     * Pixels between unlabelled ticks. The dB ruler culls its minor ticks to
     * this; the frequency ruler fills between each pair of labels with the
     * finest 1-2-5 Hz step whose ticks stay at least this far apart.
     */
    minimumTickGap: 4,
  },

  selectionMenu: {
    background: "#2c2c2c",
    border: "#444",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
    color: "#fff",
    fontSize: "13px",
    textColor: "#fff",
    readOnlyTextColor: "#666",
    mouseEnterBackground: "#404040",
    mouseLeaveBackground: "transparent",
    separatorBackground: "#444",
    detailsTextColor: "#999",
    detailsFontSize: "11px",
  },

  // Selection. selectionBorder is the fallback colour for a region group with
  // no colour of its own — in practice every group gets one automatically
  // (see regionGroupColors), so it mostly matters for a group created
  // directly through Timeline rather than AudioDAW.createRegionGroup.
  selectionBorder: "#FFC107",
  selectionBorderWidth: 2,
  selectionHandleWidth: 8,
  /** Alpha applied to a region group's colour for its fill (border/handles stay opaque). */
  selectionFillOpacity: 0.2,
  /**
   * Cycling palette `createRegionGroup()` assigns colours from, in order,
   * unless the host passes one explicitly. The same pattern `track.color`
   * would use if tracks auto-assigned a colour too — they don't, but region
   * groups are drawn side by side far more often, so telling them apart at a
   * glance matters more here.
   */
  regionGroupColors: [
    "#FFC107",
    "#4CAF50",
    "#E91E63",
    "#2196F3",
    "#FF5722",
    "#9C27B0",
    "#00BCD4",
    "#8BC34A",
  ],
  /**
   * Opacity for a visible-but-not-active group's regions in "full" display
   * mode — full detail, clearly dimmed, so it's unambiguous which group a
   * drag would actually edit.
   */
  inactiveRegionGroupOpacity: 0.35,
  /** Thickness, in px, of the edge-bar a non-active group draws in "minimized" display mode. */
  regionGroupBarHeight: 6,
  /**
   * Region text — the start/end/duration readout on hover, and the group name.
   * A group's own `textColor` wins over these; these win over nothing. Text
   * used to draw in the region's colour, which on a yellow or cyan group over
   * the light ruler was unreadable, so both default to black on a backing chip
   * (`regionLabelBackground`) that keeps black legible over the dark ruler too.
   * Set the chip to "transparent" to drop it.
   */
  regionLabelColor: "#000000",
  regionLabelFont: "10px monospace",
  regionLabelBackground: "rgba(255, 255, 255, 0.85)",
  regionGroupNameColor: "#000000",
  regionGroupNameFont: "bold 11px sans-serif",
  /**
   * Which edge of the canvas the "minimized" display mode's bar sits against.
   * "top" sits in the empty padding above the first track lane; "bottom" sits
   * in the empty padding just above the ruler strip on the main canvas, or
   * the bottom edge of the minimap (which has no ruler to avoid).
   */
  regionGroupBarPosition: "top", // "top" | "bottom"
};

/**
 * Dark theme.
 *
 * Mostly a straight swap of the light values, but the reason it exists is the
 * spectrogram: blending a bright, mostly-warm image over the light theme's
 * `#f1f1f1` track background washes the midpoint toward pink. Over a dark track
 * the same blend reads as the spectrogram emerging from behind the waveform,
 * which is what the opacity control is trying to express.
 *
 * Merge over the defaults — `setTheme` already spreads onto DEFAULT_THEME, so a
 * partial is enough and unlisted keys keep working when new ones are added.
 */
export const DARK_THEME = {
  background: "#101216",
  trackBackground: "#191c21",
  trackBackgroundDragging: "rgba(74, 163, 255, 0.18)",
  rulerBackground: "#16181c",
  minimapBackground: "#0c0e11",
  minimapTrackBackground: "#262b32",

  gridColor: "rgba(255, 255, 255, 0.05)",

  defaultWaveformColor: "#4aa3ff",

  trackBorderColor: "#4aa3ff",

  trackLabelColor: "#8b929e",

  offsetDisplayBackground: "rgba(0, 0, 0, 0.75)",
  offsetDisplayColor: "#4ec9a5",

  loadingTextColor: "#8b929e",

  rulerTickColor: "#454c57",
  rulerTextColor: "#8b929e",

  playheadColor: "#ff5c8a",

  minimapWaveformOpacity: 0.7,
  minimapViewportFill: "rgba(255, 255, 255, 0.10)",
  minimapViewportStroke: "#4ec9a5",
  minimapPlayheadColor: "#ff5c8a",

  scrollbar: {
    width: 16,
    minThumbSize: 40,
    trackColor: "rgba(255, 255, 255, 0.04)",
    thumbColor: "rgba(255, 255, 255, 0.18)",
    thumbColorActive: "rgba(255, 255, 255, 0.35)",
    thumbBorder: "rgba(0, 0, 0, 0.4)",
    showVertical: true,
    showHorizontal: true,
    customDrawFunction: null,
  },

  selectionBorder: "#FFC107",
  selectionFillOpacity: 0.14,

  scales: {
    background: "transparent",
    textColor: "#d3d9e2",
    centerTextColor: "#ffffff",
    tickColor: "#aab3c0",
    minorTickColor: "#6e7783",
    centerLineColor: "#e2e7ef",
    font: "10px system-ui, sans-serif",
    majorTickLength: 8,
    minorTickLength: 4,
    labelGap: 4,
    tickAlign: "left",
    padding: 2,
    minimumLabelGap: 1.6,
    minimumTickGap: 4,
  },

  selectionMenu: {
    background: "#1e2126",
    border: "#2f343c",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.6)",
    color: "#e6e8ec",
    fontSize: "13px",
    textColor: "#e6e8ec",
    readOnlyTextColor: "#5a616b",
    mouseEnterBackground: "#2a2f37",
    mouseLeaveBackground: "transparent",
    separatorBackground: "#2f343c",
    detailsTextColor: "#8b929e",
    detailsFontSize: "11px",
  },
};
